import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  BookOpen, 
  Play, 
  Clock, 
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  Video,
  FileText,
  Lock,
  Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const difficultyColors = {
  beginner: "bg-emerald-100 text-emerald-700",
  intermediate: "bg-amber-100 text-amber-700",
  advanced: "bg-red-100 text-red-700"
};

export default function CourseDetailPage({ user }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourseDetail();
  }, [courseId]);

  const fetchCourseDetail = async () => {
    try {
      const response = await fetch(`${API}/learning/courses/${courseId}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourse(data.course);
        setLessons(data.lessons || []);
        setCategory(data.category);
      } else if (response.status === 404) {
        navigate("/learning");
        toast.error("Course not found");
      }
    } catch (error) {
      console.error("Failed to fetch course:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hours`;
  };

  const completedCount = lessons.filter(l => l.completed).length;
  const progressPercent = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0;

  // Find first incomplete lesson to continue
  const nextLesson = lessons.find(l => !l.completed) || lessons[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="course-detail-page">
      {/* Back Link */}
      <Link 
        to="/learning"
        className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Back to Learning Center
      </Link>

      {/* Course Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Thumbnail */}
          <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl overflow-hidden mb-6">
            {course.thumbnail_url ? (
              <img 
                src={course.thumbnail_url} 
                alt={course.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-16 h-16 text-slate-300" />
              </div>
            )}
          </div>

          {/* Course Info */}
          <div className="space-y-4">
            {category && (
              <Badge variant="outline">{category.name}</Badge>
            )}
            
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              {course.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <Badge className={difficultyColors[course.difficulty]}>
                {course.difficulty}
              </Badge>
              {course.duration_minutes > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDuration(course.duration_minutes)}
                </span>
              )}
              <span>{lessons.length} lessons</span>
            </div>

            <p className="text-slate-600 leading-relaxed">
              {course.description}
            </p>

            {!course.is_published && (
              <Badge className="bg-amber-500">This course is not yet published</Badge>
            )}
          </div>
        </div>

        {/* Progress Card */}
        <div>
          <Card className="sticky top-6">
            <CardContent className="p-6">
              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Your Progress</span>
                  <span className="text-sm text-slate-500">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <p className="text-xs text-slate-500 mt-2">
                  {completedCount} of {lessons.length} lessons completed
                </p>
              </div>

              {progressPercent === 100 ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Award className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="font-medium text-emerald-600">Course Completed!</h3>
                  <p className="text-sm text-slate-500 mt-1">Great job finishing this course</p>
                </div>
              ) : nextLesson ? (
                <Button 
                  className="w-full"
                  size="lg"
                  onClick={() => navigate(`/learning/lessons/${nextLesson.lesson_id}`)}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {completedCount > 0 ? "Continue Learning" : "Start Course"}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lessons List */}
      <div>
        <h2 className="font-heading text-xl font-bold mb-4">Course Content</h2>
        
        <Card>
          <CardContent className="divide-y">
            {lessons.map((lesson, index) => (
              <div 
                key={lesson.lesson_id}
                className={`flex items-center gap-4 py-4 cursor-pointer hover:bg-slate-50 -mx-6 px-6 transition-colors ${
                  index === 0 ? "-mt-6 pt-6" : ""
                } ${index === lessons.length - 1 ? "-mb-6 pb-6" : ""}`}
                onClick={() => navigate(`/learning/lessons/${lesson.lesson_id}`)}
                data-testid={`lesson-row-${lesson.lesson_id}`}
              >
                {/* Completion Status */}
                <div className="flex-shrink-0">
                  {lesson.completed ? (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-500">{index + 1}</span>
                    </div>
                  )}
                </div>

                {/* Lesson Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {lesson.content_type === "video" ? (
                      <Video className="w-4 h-4 text-slate-400" />
                    ) : (
                      <FileText className="w-4 h-4 text-slate-400" />
                    )}
                    <h3 className={`font-medium truncate ${lesson.completed ? "text-slate-500" : ""}`}>
                      {lesson.title}
                    </h3>
                    {!lesson.is_published && (
                      <Badge variant="outline" className="text-xs">Draft</Badge>
                    )}
                  </div>
                  {lesson.description && (
                    <p className="text-sm text-slate-500 truncate mt-1">{lesson.description}</p>
                  )}
                </div>

                {/* Duration */}
                {lesson.duration_minutes > 0 && (
                  <span className="text-sm text-slate-400 flex-shrink-0">
                    {formatDuration(lesson.duration_minutes)}
                  </span>
                )}

                <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              </div>
            ))}

            {lessons.length === 0 && (
              <div className="py-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No lessons available yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
